import './index.scss';

import { Component, ContextType, createRef, DragEvent, ReactNode } from 'react';

import t from 'utils/i18n';
import files from 'utils/files';
import ToastsContext from '../../BraxToasts/context';
import { FileWithDimension, UncontrolledField } from '..';
import { BraxIcon } from '../../BraxIcon';
import { BraxUncontrolledField } from '../BraxUncontrolledField';
import BraxImageEditor from '../../BraxImageEditor';

export type BraxUncontrolledUploadBoxFieldProps = UncontrolledField<string | File | File[] | null> & {
    label: ReactNode,
    hint?: ReactNode,
    accept?: string,
    multiple: boolean,
    editable: boolean,
    droppable: boolean,
    maxWidth?: number,
    maxHeight?: number,
    required?: boolean,
    onChange?: (value: FileWithDimension[] | null) => void
};

type BraxUncontrolledUploadBoxFieldState = {
    dragging: boolean,
    editing: boolean,
    preview: string | null
};

export class BraxUncontrolledUploadBoxField extends Component<BraxUncontrolledUploadBoxFieldProps, BraxUncontrolledUploadBoxFieldState> {

    static defaultProps = {
        disabled: false,
        status: null,
        multiple: false,
        editable: true,
        droppable: true
    };

    state: BraxUncontrolledUploadBoxFieldState = {
        dragging: false,
        editing: false,
        preview: null
    };

    static contextType = ToastsContext;

    declare context: ContextType<typeof ToastsContext>;

    inputRef = createRef<HTMLInputElement>();

    imageEditorRef = createRef<BraxImageEditor>();

    async componentDidMount() {
        this.setState({ preview: await this.getPreview(this.props.value) });
    }

    async componentDidUpdate(prevProps: BraxUncontrolledUploadBoxFieldProps, prevState: BraxUncontrolledUploadBoxFieldState) {
        if (prevState.preview !== this.state.preview && prevState.preview)
            URL.revokeObjectURL(prevState.preview)

        if (prevProps.value !== this.props.value)
            this.setState({ preview: await this.getPreview(this.props.value) });
    }

    async getPreview(value: string | File | File[] | null | undefined) {
        if (!value)
            return null;

        if (typeof value === 'string')
            try {
                const file = await files.loadAsBlob(value);

                return await files.isImageType(file) ? URL.createObjectURL(file) : null;
            } catch (error) {
                return null;
            }

        const file = Array.isArray(value) ? value[0] : value;

        return file && await files.isImageType(file) ? URL.createObjectURL(file) : null;
    }

    isAccpeted(files: File[]) {
        if (!this.props.accept)
            return true;

        const patterns = this.props.accept.split(',');

        if (patterns.length === 0)
            return true;

        for (const file of files)
            if (!patterns.some(pattern => pattern === file.type || pattern.endsWith('/*') && pattern.slice(0, -2) === file.type.slice(0, file.type.indexOf('/'))))
                return false;

        return true;
    }

    onPreviewLoadError() {
        if (this.state.preview)
            this.setState({ preview: null });
    }

    handleDragState(event: DragEvent<HTMLDivElement>, dragging: boolean) {
        event.preventDefault();

        event.stopPropagation();

        this.setState({ dragging });
    }

    drop(event: DragEvent<HTMLDivElement>) {
        this.handleDragState(event, false);

        this.change(event.dataTransfer.files);
    }

    async change(fileList: FileList | null) {
        if (!fileList?.length)
            return;

        const list = this.props.multiple ? Array.from(fileList) : [fileList[0]];

        if (this.isAccpeted(list)) {
            this.setState({ editing: this.props.editable });

            for (const file of list)
                Object.assign(file, { dimensions: await files.getImageSizes(file) });

            this.props.onChange?.(list);
        } else {
            this.context.create({
                type: 'destructive',
                title: t('Arquivo inválido'),
                description: t('Somente arquivos de imagem são permitidos.')
            });
        }
    }

    async crop(blob: Blob) {
        const file = new File(
            [blob],
            typeof this.props.value === 'string'
                ? this.props.value
            : this.props.value && this.props.value[0]
                ? this.props.value[0].name
                : '',
            {
                type: blob.type,
                lastModified: Date.now()
            }
        );

        this.props.onChange?.([Object.assign(file, { dimensions: await files.getImageSizes(file) })]);

        this.setState({ editing: false });
    }

    open() {
        this.inputRef.current?.click();
    }

    async cropImage() {
        await this.imageEditorRef.current?.save();
    }

    render() {
        const { id, label, editable, maxWidth, maxHeight, required, status, hint, value, droppable, onChange, ...props } = this.props;

        return (
            <BraxUncontrolledField id={id}>
                { ({ id }) => (
                    <div
                        className={`brax-upload-box ${status} ${this.state.dragging ? 'dragging' : ''}`}
                        onDragOver={this.props.droppable ? event => this.handleDragState(event, true) : undefined}
                        onDragEnter={this.props.droppable ? event => this.handleDragState(event, true) : undefined}
                        onDrop={this.props.droppable ? event => this.drop(event) : undefined}
                        onDragLeave={this.props.droppable ? event => this.handleDragState(event, false) : undefined}
                        onDragEnd={this.props.droppable ? event => this.handleDragState(event, false) : undefined}
                    >
                        <input
                            ref={this.inputRef}
                            id={id}
                            type="file"
                            capture="environment"
                            onChange={event => this.change(event.target.files)}
                            {...props}
                        />

                        { this.state.preview ? (
                            this.state.editing ? (
                                <BraxImageEditor
                                    ref={this.imageEditorRef}
                                    src={this.state.preview}
                                    maxWidth={maxWidth}
                                    maxHeight={maxHeight}
                                    onCrop={blob => this.crop(blob)}
                                />
                            ) : (
                                <img
                                    src={this.state.preview}
                                    alt=""
                                    onError={() => this.onPreviewLoadError()}
                                />
                            )
                        ) : null }

                        { !this.state.editing ? (
                            <label htmlFor={this.props.disabled ? undefined : id}>
                                <BraxIcon icon="upload" />

                                <span className="hint">
                                    <span>{ label } { required ? '*' : null }</span>

                                    { hint ? (
                                        <span>
                                            { status === 'error' ? <BraxIcon icon="exclamation-mark" /> : null }

                                            <span>{ hint }</span>
                                        </span>
                                    ) : null }
                                </span>
                            </label>
                        ) : null }
                    </div>
                ) }
            </BraxUncontrolledField>
        );
    }

}
